// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 16-api-conventions.md
import * as React from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import { DataList, type DataListColumn } from '@/components/greenlight/DataList';
import { MonoValue } from '@/components/greenlight/MonoValue';
import { StatusBadge, type StatusBadgeVariant } from '@/components/greenlight/StatusBadge';
import { LogDrawer, type LogSource } from '@/components/health/LogDrawer';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { api } from '@/lib/api';
import { downloadTextFile } from '@/lib/download';
import { useSessionCursorStack } from '@/lib/useSessionCursorStack';
import type { AppCrashDetail, AppCrashListItem } from '@greenlight/contract';
import { BulkActionToolbar } from './BulkActionToolbar';
import {
  crashLogUnavailableCopy,
  crashReasonLabel,
  renderAppCrashesMarkdown,
} from './appCrashesMarkdown';

const PAGE_SIZE = 25;
const CURSOR_PARAM = 'ccursor';
const LIST_KEY = 'feedback-app-crashes';

const DELIVERY: Record<
  AppCrashListItem['delivery']['status'],
  { variant: StatusBadgeVariant; label: string }
> = {
  not_sent: { variant: 'inactive', label: 'Not sent' },
  pending: { variant: 'pending', label: 'Sending' },
  sent: { variant: 'active', label: 'Sent to Shift' },
  gave_up: { variant: 'failed', label: 'Send failed' },
};

async function readCrash(id: string): Promise<AppCrashDetail> {
  const res = await api.feedback.appCrash.query({ params: { id } });
  if (res.status !== 200) throw new Error(`crash read returned ${res.status}`);
  return res.body;
}

function crashLogSource(crash: AppCrashListItem): LogSource {
  return {
    usePage() {
      const queryKey = ['feedback-app-crash', crash.id];
      const query = api.feedback.appCrash.useQuery(
        queryKey,
        { params: { id: crash.id } },
        { queryKey, enabled: crash.has_log },
      );
      const body = query.data?.status === 200 ? query.data.body : null;
      return {
        page: body ? { text: body.log_tail ?? '', next_cursor: null } : null,
        isLoading: query.isLoading,
        isError: query.isError,
        isFetching: query.isFetching,
        refetch: () => void query.refetch(),
      };
    },
    async readAll() {
      return (await readCrash(crash.id)).log_tail ?? '';
    },
  };
}

export function AppCrashesTab(): React.JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams();
  const cursor = searchParams.get(CURSOR_PARAM) ?? undefined;
  const trail = useSessionCursorStack(LIST_KEY);
  const clearTrail = trail.clear;
  React.useEffect(() => {
    if (!cursor) clearTrail();
  }, [cursor, clearTrail]);
  const [selected, setSelected] = React.useState<Map<string, AppCrashListItem>>(new Map());
  const [open, setOpen] = React.useState<AppCrashListItem | null>(null);
  const [busy, setBusy] = React.useState<'download' | 'send' | null>(null);

  const key = [LIST_KEY, cursor ?? null];
  const query = api.feedback.appCrashes.useQuery(
    key,
    { query: { limit: PAGE_SIZE, ...(cursor ? { cursor } : {}) } },
    { queryKey: key },
  );
  const items = query.data?.status === 200 ? query.data.body.items : [];
  const nextCursor = query.data?.status === 200 ? query.data.body.next_cursor : null;

  function toggleRow(item: AppCrashListItem): void {
    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(item.id)) next.delete(item.id);
      else next.set(item.id, item);
      return next;
    });
  }

  function setCursor(next: string | undefined): void {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        if (next) params.set(CURSOR_PARAM, next);
        else params.delete(CURSOR_PARAM);
        return params;
      },
      { replace: true },
    );
  }

  function goNext(): void {
    if (!nextCursor) return;
    trail.push(cursor ?? '');
    setCursor(nextCursor);
  }

  function goPrev(): void {
    const prev = trail.pop();
    setCursor(prev === '' ? undefined : prev);
  }

  async function downloadSelected(): Promise<void> {
    const ids = [...selected.keys()];
    if (ids.length === 0) return;
    setBusy('download');
    try {
      const details = await Promise.all(ids.map(readCrash));
      downloadTextFile('greenlight-app-crashes.md', renderAppCrashesMarkdown(details));
      toast.success(`Downloaded ${ids.length} crash${ids.length === 1 ? '' : 'es'}.`);
      setSelected(new Map());
    } catch {
      toast.error("Couldn't download the selected crashes. Retry in a moment.");
    } finally {
      setBusy(null);
    }
  }

  async function sendSelected(): Promise<void> {
    const ids = [...selected.keys()];
    if (ids.length === 0) return;
    setBusy('send');
    try {
      const res = await api.feedback.shareAppCrashes.mutation({ body: { ids } });
      if (res.status !== 200) throw new Error(`share returned ${res.status}`);
      const { queued, already_sent } = res.body;
      if (queued > 0) {
        toast.success(
          `Sending ${queued} crash${queued === 1 ? '' : 'es'} to Shift.` +
            (already_sent > 0 ? ` ${already_sent} had already been sent.` : ''),
        );
      } else {
        toast.info('The selected crashes have already been sent to Shift.');
      }
      setSelected(new Map());
      void query.refetch();
    } catch {
      toast.error("Couldn't send the selected crashes. Retry in a moment.");
    } finally {
      setBusy(null);
    }
  }

  const columns: DataListColumn<AppCrashListItem>[] = [
    {
      key: 'app',
      header: 'App',
      primary: true,
      render: (i) => (
        <span className="block min-w-0">
          <span className="block truncate">{i.app_name}</span>
          <span className="block truncate font-mono text-[11px] text-muted-foreground">
            {i.app_slug}
          </span>
        </span>
      ),
    },
    {
      key: 'reason',
      header: 'Reason',
      render: (i) => (
        <span className="text-[12px]">
          {crashReasonLabel(i)}
          {i.occurrence_count > 1 ? (
            <span className="text-muted-foreground"> · {i.occurrence_count} times</span>
          ) : null}
        </span>
      ),
    },
    {
      key: 'when',
      header: 'When',
      priority: 'md',
      render: (i) => (
        <span className="text-[12px] text-muted-foreground">
          {new Date(i.occurred_at).toLocaleString()}
        </span>
      ),
    },
    {
      key: 'sent',
      header: 'Shift',
      render: (i) => (
        <StatusBadge variant={DELIVERY[i.delivery.status].variant}>
          {DELIVERY[i.delivery.status].label}
        </StatusBadge>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <BulkActionToolbar
        selectionCount={selected.size}
        actions={[
          {
            label: 'Download .md',
            onClick: () => void downloadSelected(),
            busy: busy === 'download',
            busyLabel: 'Downloading…',
            primary: true,
            testId: 'app-crashes-download',
          },
          {
            label: 'Send to Shift',
            onClick: () => void sendSelected(),
            busy: busy === 'send',
            busyLabel: 'Sending…',
            primary: true,
            testId: 'app-crashes-send-to-shift',
          },
        ]}
      />

      {query.isError ? (
        <Alert variant="destructive">
          <AlertDescription>Couldn&apos;t load app crashes. Retry in a moment.</AlertDescription>
        </Alert>
      ) : query.isLoading ? (
        <div className="space-y-2" data-testid="app-crashes-loading">
          {Array.from({ length: 6 }, (_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      ) : (
        <>
          <DataList
            items={items}
            columns={columns}
            rowKey={(i) => i.id}
            ariaLabel="App crashes"
            selectedKey={open?.id}
            selectedKeys={new Set(selected.keys())}
            onToggleRow={toggleRow}
            selectionLabel={(i) => `Select ${i.app_slug} crash at ${i.occurred_at}`}
            onRowActivate={setOpen}
            empty={
              <div className="rounded-gl border border-dashed border-border p-8 text-center">
                No app crashes recorded yet. When an app&apos;s container exits abnormally, its last
                output lands here.
              </div>
            }
          />
          <div className="flex items-center justify-end gap-2">
            <Button variant="outline" size="sm" onClick={goPrev} disabled={!cursor}>
              Previous
            </Button>
            <Button variant="outline" size="sm" onClick={goNext} disabled={!nextCursor}>
              Next
            </Button>
          </div>
        </>
      )}

      <LogDrawer
        open={open !== null}
        onClose={() => setOpen(null)}
        storageKey="gl-app-crash-detail-width"
        testId="app-crash-detail"
        srDescription="App crash detail"
        title={
          <div className="min-w-0">
            <span className="font-mono text-[10.5px] uppercase tracking-[0.08em] text-muted-foreground">
              App crash · {open?.app_slug ?? '…'}
            </span>
            <p className="mt-0.5 truncate text-sm font-semibold text-foreground">
              {open ? crashReasonLabel(open) : ' '}
            </p>
          </div>
        }
        log={
          open
            ? {
                recordId: open.id,
                heading: 'Last output before the crash',
                source: crashLogSource(open),
                hasLog: open.has_log,
                emptyCopy: crashLogUnavailableCopy(null),
                caption: () =>
                  open.log_truncated
                    ? 'The newest 256 KiB, redacted. Older output was cut.'
                    : 'The whole previous-container log, redacted.',
                downloadName: `${open.app_slug}-crash-${open.occurred_at.slice(0, 19).replace(/:/g, '')}.log`,
                label: `Crash log for ${open.app_slug}`,
                noun: 'log',
                testId: 'app-crash-log',
              }
            : null
        }
      >
        {open ? (
          <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1.5 text-[12px]">
            <dt className="text-muted-foreground">App</dt>
            <dd>
              {open.app_name} <MonoValue>{open.app_slug}</MonoValue>
            </dd>
            <dt className="text-muted-foreground">Image</dt>
            <dd className="min-w-0 truncate">
              <MonoValue>{open.image}</MonoValue>
            </dd>
            <dt className="text-muted-foreground">When</dt>
            <dd>
              {new Date(open.occurred_at).toLocaleString()}
              {open.occurrence_count > 1
                ? `, then ${open.occurrence_count - 1} more by ${new Date(open.last_occurred_at).toLocaleString()}`
                : ''}
            </dd>
            <dt className="text-muted-foreground">Shift</dt>
            <dd>
              {DELIVERY[open.delivery.status].label}
              {open.delivery.sent_at
                ? ` · ${new Date(open.delivery.sent_at).toLocaleString()}`
                : ''}
            </dd>
          </dl>
        ) : null}
      </LogDrawer>
    </div>
  );
}
